import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createNurseryChild,
  fetchNurseryChild,
  fetchNurseryChildren,
  fetchNurseryDoctors,
  removeNurseryChild,
  updateNurseryChild,
} from './nursery.api';
import type { NurseryChildPayload } from './types';

export const nurseryKeys = {
  all: ['nursery'] as const,
  list: (page: number, limit: number, search: string, sortBy: string | null = null, sortOrder: 'asc' | 'desc' = 'asc') =>
    [...nurseryKeys.all, 'list', { page, limit, search, sortBy, sortOrder }] as const,
  detail: (childId: string) => [...nurseryKeys.all, 'detail', childId] as const,
  doctors: () => [...nurseryKeys.all, 'doctors'] as const,
};

export const useNurseryChildren = (page: number, limit: number, search: string, sortBy: string | null = null, sortOrder: 'asc' | 'desc' = 'asc') => useQuery({
  queryKey: nurseryKeys.list(page, limit, search, sortBy, sortOrder),
  queryFn: () => fetchNurseryChildren(page, limit, search, sortBy, sortOrder),
});

export const useNurseryChild = (childId: string | undefined) => useQuery({
  queryKey: nurseryKeys.detail(childId ?? ''),
  queryFn: () => fetchNurseryChild(childId as string),
  enabled: Boolean(childId),
});

export const useNurseryDoctors = () => useQuery({
  queryKey: nurseryKeys.doctors(),
  queryFn: fetchNurseryDoctors,
  staleTime: 60_000,
});

export const useCreateNurseryChild = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createNurseryChild,
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: nurseryKeys.all }),
  });
};

export const useUpdateNurseryChild = (childId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: NurseryChildPayload) => updateNurseryChild(childId, payload),
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: nurseryKeys.all }),
  });
};

export const useRemoveNurseryChild = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: removeNurseryChild,
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: nurseryKeys.all }),
  });
};
